import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientIdClientContractsId200,
  GetApiV1ThreeLetterCodePartnerAdminsContractPlans200,
} from '@fukuri-admins/apis/client/gen/model';
import type { EditClientContractTermPresentation } from '../../presentations';

/**
 * converter が API レスポンスから導出できる Presentation Props のみを対象にする
 *
 * onSubmit / onCancel / isButtonDisabled は hooks 層が組み立てるハンドラ・状態であり、
 * API レスポンスからは導出できないため Omit で除外する。
 */
type PresentationProps = Omit<
  React.ComponentProps<typeof EditClientContractTermPresentation>,
  'onSubmit' | 'onCancel' | 'isButtonDisabled'
>;

type Args = {
  /** API レスポンス（useFetch の data をそのまま受け取る） */
  data: GetApiV1ThreeLetterCodePartnerAdminsClientsClientIdClientContractsId200;
  /** 契約プラン一覧データ */
  contractPlansData: GetApiV1ThreeLetterCodePartnerAdminsContractPlans200;
};

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * 企業契約期間データを EditClientContractTerm の Presentation Props に変換する
 *
 * - data.client_contract から単一の契約期間を受け取る
 * - パートナー販売手数料・Leafea収益単価は PartnerAdminContractPlan 型自体にフィールドが存在しないため参照しない（非表示制御は不要）
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data,
  contractPlansData,
}) => {
  const contractTerm = data.client_contract;

  return {
    clientName: contractTerm.client.name,
    contractPlanName: contractTerm.contract_plan.name,
    kindBadge:
      contractTerm.contract_plan.kind === 'custom'
        ? { label: '個社', status: 'info' as const }
        : { label: '標準', status: 'neutral' as const },
    giftPlan: !contractTerm.contract_plan.gift_plan
      ? '-'
      : `${contractTerm.contract_plan.gift_plan.amount}円（${contractTerm.contract_plan.gift_plan.months_interval}ヶ月に1回 利用可）`,
    customerChargeUnitPrice: `${contractTerm.contract_plan.customer_charge_unit_price}円`,
    taxRatePercent: `${contractTerm.contract_plan.tax_rate_percent}%`,
    contractPlanOptions: contractPlansData.contract_plans
      .filter(plan => plan.discontinued_at === 0)
      .map(plan => ({ value: String(plan.id), label: plan.name })),
    isStartedOnDisabled: !contractTerm.is_started_on_editable,
    isContractPlanDisabled: !contractTerm.is_contract_plan_editable,
    endedOnFromDate: new Date(contractTerm.ended_on_min),
  };
};
